/* eslint-disable react-refresh/only-export-components */
import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { useApiData } from "./ApiData";
import type { Notification } from "@/types";

type NotificationsContextType = {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  reload: () => void;
};

const NotificationsContext = createContext<NotificationsContextType>({
  notifications: [],
  unreadCount: 0,
  loading: false,
  markAsRead: async () => {},
  markAllAsRead: async () => {},
  reload: () => {},
});

type NotificationsProviderProps = {
  children: ReactNode;
};

export const useNotifications = (): NotificationsContextType => {
  return useContext(NotificationsContext);
};

export const NotificationsProvider = ({ children }: NotificationsProviderProps) => {
  const { get, patch } = useApiData();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [version, setVersion] = useState(0);

  const reload = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    const call = async () => {
      setLoading(true);
      const result = await get("/notifications");
      if (result) {
        setNotifications(result);
      }
      setLoading(false);
    };
    call();
  }, [get, version]);

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const markAsRead = useCallback(
    async (id: string) => {
      await patch(`/notifications/${id}/read`, {});
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
    },
    [patch]
  );

  const markAllAsRead = useCallback(async () => {
    await patch("/notifications/read-all", {});
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
  }, [patch]);

  return (
    <NotificationsContext.Provider value={{ notifications, unreadCount, loading, markAsRead, markAllAsRead, reload }}>
      {children}
    </NotificationsContext.Provider>
  );
};
